import { useCallback } from 'react'

import { useCADPageStore } from '@/state/cad'
import { getDescendants } from '@/state/utils/assembly'
import { useOperationSteps } from '../queries/operation_steps'
import { useAssemblyTreeQuery } from '@/pages/CADPage/queries'
import { useCADQuery } from '@/services/queries/cads'

export const useGetInclusiveGroupIds = () => {
  const { isLoading, steps } = useOperationSteps()
  const operationStep = useCADPageStore((state) => state.operationStep)

  if (isLoading || !operationStep) return null
  const activeStep = steps.find((step) => step.id === operationStep.stepId)
  const stepIsActive = Boolean(operationStep?.isActive)

  if (!activeStep || !stepIsActive) return null
  const stepNumber = activeStep?.order_number

  return [
    ...new Set(steps.slice(0, stepNumber).flatMap((s) => s.assembly_group_ids)),
  ]
}

export const useGetActiveStepInclusiveGroupIds = () => {
  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const operationStep = useCADPageStore((state) => state.operationStep)
  const activeStep = steps.find((step) => step.id === operationStep?.stepId)
  const activeParentId = activeStep?.parent
  const stepNumber = activeStep?.order_number || 0
  const gltf = cadData.gltf

  const inclusiveGroupIds = Array.from(
    new Set(
      steps
        .slice(0, stepNumber)
        .filter((s) => s.parent === activeParentId)
        .flatMap((s) => {
          return assemblyTree && gltf
            ? gltf.getVisiblePathOfGroupIds({
                assemblyGroupIds: s.assembly_group_ids,
                assemblyTree,
              })
            : []
        }),
    ),
  )

  const getActiveStepInclusiveGroupAndDescendantIds = useCallback(() => {
    if (!assemblyTree || !inclusiveGroupIds) return []

    // @ts-expect-error - converting from set to array
    const groupIds = Array.from(inclusiveGroupIds) as string[]

    return Array.from(
      new Set([
        ...groupIds,
        ...groupIds.flatMap((id: string) => {
          const treeNode = assemblyTree.nodes.find((n) => n.uuid === id)
          return treeNode
            ? getDescendants(assemblyTree, treeNode).map((n) => n.uuid)
            : []
        }),
      ]),
    )
  }, [assemblyTree, inclusiveGroupIds])

  return {
    isLoading: isLoadingSteps,
    data: { inclusiveGroupIds },
    getActiveStepInclusiveGroupAndDescendantIds,
  }
}
